import React from 'react'
import { observer } from 'mobx-react-lite'

import {
  Drawer,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useTransactionStore,
} from '../../../common'
import { type RepeatingTransaction, type Transaction, RepeatingType } from '../../../core'

import {
  expenseFilter,
  expenseFilterSearch,
  incomeFilter,
  incomeFilterSearch,
} from '../../../transactions/utils/Filters'

import { SelectTransaction } from '../shared/recurringsettings/SelectTransaction'
import { useTransactionsUiStore } from '../../../transactions'

interface LinkTransactionDrawerProps {
  isOpen: boolean
  onClose: () => void
  recurringTransaction: RepeatingTransaction
}

const buildFilter =
  (recurringTransaction: RepeatingTransaction, searchValue?: string) => (t: Transaction) => {
    if (t.repeating_transaction_guid) return false

    const isIncome = recurringTransaction.repeating_transaction_type === RepeatingType.Income

    if (searchValue) {
      return isIncome ? incomeFilterSearch(t, searchValue) : expenseFilterSearch(t, searchValue)
    }

    return isIncome ? incomeFilter(t) : expenseFilter(t)
  }

export const LinkTransactionDrawer = observer(
  ({ isOpen, onClose, recurringTransaction }: LinkTransactionDrawerProps) => {
    const { recurring: copy } = useGlobalCopyStore()
    const { markRecurrenceAsPaid } = useRecurringTransactionsStore()
    const { filter, setFilter, transactions } = useTransactionStore()

    const { setSearchValue } = useTransactionsUiStore()

    React.useEffect(() => {
      if (isOpen) {
        setFilter({ ...filter, custom: buildFilter(recurringTransaction) })
      } else {
        setSearchValue('')
      }
    }, [isOpen, recurringTransaction])

    const handleSearch = (searchValue: string) => {
      setFilter({ ...filter, custom: buildFilter(recurringTransaction, searchValue) })
    }

    const handleSelected = async (guid: string) => {
      const transaction = transactions.find((t) => t.guid === guid)
      if (transaction) {
        await markRecurrenceAsPaid(recurringTransaction.guid, transaction)
        onClose()
      }
    }

    return (
      <Drawer
        ariaLabelClose={copy.close_drawer_aria}
        isOpen={isOpen}
        onClose={onClose}
        title={copy.link_transaction_button}
      >
        <SelectTransaction onSearch={handleSearch} onSelected={handleSelected} />
      </Drawer>
    )
  },
)
